import { EApplicantType, EFinancingProgram, getECreditApplicationStepList, getERequiredFromList } from '@if/core/types'
import {
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { useCompletedSteps, useHasCoapplicant } from '@src/data/selectors/credit-selectors'
import { debounce, every } from 'lodash-es'
import { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ActionsDialog, InputTextField, MultipleSelectCheckmarks, SelectComponent } from '../../../components'
import {
  useApplicantTypeEnum,
  useCurrentLang,
  useDocumentTypesAddableManually,
} from '../../../data/selectors/app-config-selectors'
import { SelectValueListItem } from '../../../data/types'
import { DocumentRequired } from '../../../data/types/DocumentSchema'

interface Props {
  open: boolean
  creditApplicationId: string
  onConfirm: (data: DocumentRequired[]) => void
  onCancel: () => void
}

const DocumentsRequestDialog = ({
  open,
  creditApplicationId,
  onConfirm,
  onCancel,
}: Props) => {
  const { t } = useTranslation()
  const { financingProgramId } = useParams<{ financingProgramId: EFinancingProgram }>()
  const documentTypes = useDocumentTypesAddableManually(financingProgramId!)
  const hasCoapplicant = useHasCoapplicant()
  const applicantTypeEnum = useApplicantTypeEnum()
  const lang = useCurrentLang()
  const [selectedDocumentsId, setselecteDocumentsId] = useState<number[]>([])
  const [textFilter, setTextFilter] = useState<string>('')
  const [filteredDocs, setFilteredDocs] = useState<number[]>([])
  const handleOnConfirm = useCallback(() => {
    const docs: DocumentRequired[] = []

    selectedDocumentsId.forEach((typeId) => {
      const requiredBeforeStepElement = document.getElementById(`${typeId}.requiredBeforeStep`) as HTMLInputElement
      const applicantsElement = document.getElementById(`${typeId}.applicants`) as HTMLInputElement
      const noteElement = document.getElementById(`${typeId}.note`) as HTMLInputElement
      const requiredFromElement = document.getElementById(`${typeId}.requiredFrom`) as HTMLInputElement

      if (applicantsElement) {
        const applicant = applicantsElement.value.split(',')
        applicant.forEach((app) => {
          const doc = {
            creditApplicationId,
            financingProgramId,
            typeId,
            applicantType: app === '' ? EApplicantType.Applicant : app,
            requiredBeforeStep: requiredBeforeStepElement.value,
            note: noteElement.value,
            requiredFrom: requiredFromElement?.value,
          } as DocumentRequired
          docs.push(doc)
        })
      } else {
        const doc = {
          creditApplicationId,
          financingProgramId,
          typeId,
          applicantType: null,
          requiredBeforeStep: requiredBeforeStepElement.value,
          note: noteElement.value,
          requiredFrom: requiredFromElement?.value,
        } as DocumentRequired
        docs.push(doc)
      }
    })

    onConfirm(docs)
    setselecteDocumentsId([])
  }, [onConfirm, selectedDocumentsId, creditApplicationId, financingProgramId])

  const handleOnChange = useCallback(
    (checked: boolean, docId: number) => {
      const docs = selectedDocumentsId.filter((x) => x !== docId)
      if (checked) setselecteDocumentsId([...docs, docId])
      else setselecteDocumentsId(docs)
    },
    [selectedDocumentsId],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterDocs = useCallback(
    debounce((filters) => {
      const match = Object.values(documentTypes).filter((type) => {
        return every(filters, (f: string) => type[lang].toString().toLocaleLowerCase().includes(f.toLocaleLowerCase()))
      })
      setFilteredDocs(match.map((x) => Number(x.id)))
    }, 200),
    [documentTypes],
  )

  useEffect(() => {
    if (open) {
      if (textFilter.trim() !== '') {
        filterDocs(textFilter.split(' '))
      } else setFilteredDocs(Object.keys(documentTypes).map((id) => Number(id)))
    }
  }, [textFilter, open, documentTypes, filterDocs])

  const featureFlags = useFeatureFlags()
  const completedSteps = useCompletedSteps()
  const creditApplicationStepOptions = useMemo<SelectValueListItem[]>(
    () =>
      getECreditApplicationStepList().map((value) => ({
        label: t(`ECreditApplicationStep.${value}`, { ns: 'enum' }),
        value,
        disabled: featureFlags.useTaskFollowUpOnPPBKApp && completedSteps.has(value),
      })),
    [t, featureFlags.useTaskFollowUpOnPPBKApp, completedSteps],
  )
  const requiredFromOptions = useMemo<SelectValueListItem[]>(
    () =>
      getERequiredFromList().map((value) => ({
        label: t(`ERequiredFrom.${value}`, { ns: 'enum' }),
        value,
      })),
    [t],
  )

  return (
    <ActionsDialog
      title={
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={3}>
          <Typography>{t('document.request')}</Typography>
          <TextField
            name={t('common.filter')}
            onChange={(e) => setTextFilter(e.target.value)}
            value={textFilter}
            label={t('common.filter')}
          />
        </Stack>
      }
      open={open}
      onCancel={() => {
        onCancel()
        setselecteDocumentsId([])
      }}
      onConfirm={handleOnConfirm}
    >
      <TableContainer component={Paper} sx={{ minWidth: '100vh', maxHeight: '70vh', mt: 2 }}>
        <Table>
          <TableBody>
            {filteredDocs
              .map((id) => {
                return {
                  id,
                  label: documentTypes[id][lang],
                  mustSpecifyApplicant: documentTypes[id].mustSpecifyApplicant,
                }
              })
              .sort((a, b) => a.label.toString().localeCompare(b.label.toString()))
              .map((item) => (
                <TableRow key={item.id}>
                  <TableCell align="right">
                    <Checkbox
                      checked={selectedDocumentsId.find((x) => x === item.id) !== undefined}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        handleOnChange(event.target.checked, item.id)
                      }}
                    />
                  </TableCell>
                  <TableCell align="left">{item.label}</TableCell>
                  <TableCell align="left">
                    {item.mustSpecifyApplicant === true && (
                      <MultipleSelectCheckmarks
                        {...{ id: `${item.id}.applicants` }}
                        title={`${t('common.applicant')}(s)`}
                        content={Object.values(applicantTypeEnum).map((val) => {
                          const data: SelectValueListItem = {
                            label: val[lang],
                            value: val.id,
                          }
                          return data
                        })}
                        disabled={!hasCoapplicant}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: '15%' }} align="left">
                    <SelectComponent
                      {...{ id: `${item.id}.requiredBeforeStep` }}
                      items={creditApplicationStepOptions}
                      label={t('common.requiredFor')}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: '15%' }} align="left">
                    <SelectComponent
                      {...{ id: `${item.id}.requiredFrom` }}
                      items={requiredFromOptions}
                      label={t('common.requiredFrom')}
                    />
                  </TableCell>
                  <TableCell align="left" sx={{ width: '35%' }}>
                    <InputTextField
                      placeholder={t('common.clientVisible')}
                      id={`${item.id}.note`}
                      label={`${t('common.comment')} - ${t('common.clientVisible')}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ActionsDialog>
  )
}

export default memo(DocumentsRequestDialog)
